// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useMemo } from 'react';

import { type WorkOrderStateName, workOrderStateNames, type ShopRole, type WorkOrder } from '@evolvemep/api-ts';

import { isNotNil } from 'helpers/isNotNil';
import { strongEntries } from 'helpers/strongEntries';
import { useUser } from 'hooks/user/useUser';

// All possible actions we can take on a Work Order in the front-end
export type WorkOrderAction =
  | 'submit'
  | 'transfer'
  | 'confirmTransfer'
  | 'reject'
  | 'setAsPending'
  | 'approve'
  | 'release'
  | 'cancel'
  | 'requestCancel'
  | 'denyCancel'
  | 'acceptCancel'
  | 'block'
  | 'actionTasks'
  | 'startPauseTasks'
  | 'editFacility'
  | 'editItems'
  | 'editTasks'
  | 'editWorkOrderId'
  | 'editProperties'
  | 'editOnSiteDate'
  | 'delete';

type WorkOrderTruthByRole = Partial<Record<ShopRole, readonly WorkOrderStateName[]>>;

/**
 * A map of `action` to the statuses that allow that action.
 * This is intended to be a code-ified version of the Work Order truth table.
 *
 * If roles are listed, the action can only be performed if the user has that role.
 * If roles are not listed, any user (with the right license ofc) can perform the action.
 *
 * Refer to https://github.com/eVolveMEP/FWP/wiki/Truth-Table-%E2%80%90-Work-Order-By-Status for documentation on this.
 * Note that the backend may have some disparities. Actions that allow "pending" tend to also allow "submitted".
 */
export const workOrderTruthTable: Record<WorkOrderAction, WorkOrderStateName[] | WorkOrderTruthByRole> = {
  submit: ['Draft', 'Rejected', 'Canceled'],
  reject: {
    ['Shop.FullAccess']: ['Pending', 'Approved', 'Blocked'],
  },
  setAsPending: {
    ['Shop.FullAccess']: ['Submitted'],
  },
  approve: {
    ['Shop.FullAccess']: ['Pending'],
  },
  release: {
    ['Shop.FullAccess']: ['Blocked', 'Approved'],
  },
  // Only limited access & shop floor users can "Cancel" Work Orders
  cancel: {
    ['Shop.LimitedAccess']: ['Pending', 'Submitted', 'Approved'],
    ['Shop.ShopFloor']: ['Pending', 'Submitted', 'Approved'],
  },
  requestCancel: {
    ['Shop.LimitedAccess']: ['Not Started', 'In Progress', 'Blocked'],
    ['Shop.ShopFloor']: ['Not Started', 'In Progress', 'Blocked'],
  },
  acceptCancel: {
    ['Shop.FullAccess']: ['Request for Cancel'],
  },
  denyCancel: {
    ['Shop.FullAccess']: ['Request for Cancel'],
  },
  // You can also block via "Cancel Request",
  // but this happens via `cancel/accept` when the WO is already released
  block: {
    ['Shop.FullAccess']: ['Not Started', 'In Progress'],
  },
  editFacility: ['Draft', 'Canceled', 'Rejected'],
  transfer: {
    ['Shop.FullAccess']: ['Pending', 'Approved'],
  },
  confirmTransfer: {
    ['Shop.FullAccess']: ['Transferred'],
  },
  /** This represents any task workflow step, such as start, pause, complete, or resume */
  actionTasks: {
    ['Shop.ShopFloor']: ['Blocked', 'Not Started', 'In Progress', 'Completed'],
    ['Shop.FullAccess']: ['Blocked', 'Not Started', 'In Progress', 'Completed'],
  },
  /**
   * Time tracking specifically. A blocked work order still allows the rest of `actionTasks` — completing,
   * resuming, uncompleting — but no time may be accrued against it.
   */
  startPauseTasks: {
    ['Shop.ShopFloor']: ['Not Started', 'In Progress', 'Completed'],
    ['Shop.FullAccess']: ['Not Started', 'In Progress', 'Completed'],
  },
  editItems: {
    ['Shop.LimitedAccess']: ['Draft', 'Rejected', 'Canceled'],
    ['Shop.ShopFloor']: ['Draft', 'Rejected', 'Canceled'],
    ['Shop.FullAccess']: ['Draft', 'Pending', 'Blocked', 'Rejected', 'Approved'],
  },
  editTasks: {
    ['Shop.FullAccess']: ['Draft', 'Pending', 'Blocked', 'Rejected', 'Approved'],
  },
  editProperties: {
    ['Shop.LimitedAccess']: workOrderStateNames,
    ['Shop.ShopFloor']: workOrderStateNames,
    ['Shop.FullAccess']: workOrderStateNames,
  },
  editWorkOrderId: {
    ['Shop.FullAccess']: workOrderStateNames.filter((s) => s !== 'Completed'),
  },
  // Currently, On-Site Date can be edited in ANY state; this may change when Shipping goes live
  editOnSiteDate: {
    ['Shop.LimitedAccess']: workOrderStateNames,
    ['Shop.ShopFloor']: workOrderStateNames,
    ['Shop.FullAccess']: workOrderStateNames,
  },
  delete: ['Draft', 'Canceled', 'Rejected'],
} as const;

/** Company Admins can automatically perform any actions on work orders. This is a list of actions to exclude. */
const preventAdminActions: WorkOrderAction[] = ['requestCancel'];

export const useGetWorkOrderStatesFromTruthTable = () => {
  const { userHasRoles } = useUser();
  const getWorkOrderStatesFromTruthTable = useCallback(
    (action: WorkOrderAction): readonly WorkOrderStateName[] => {
      const truth = workOrderTruthTable[action];
      if (Array.isArray(truth)) {
        return truth;
      }
      return strongEntries(truth as WorkOrderTruthByRole)
        .filter(([a]) => userHasRoles(a))
        .flatMap(([, b]) => b)
        .filter(isNotNil);
    },
    [userHasRoles],
  );

  return { getWorkOrderStatesFromTruthTable };
};

/**
 * Checks if we can perform `action` on a work order in a given `workOrder`.
 */
export const useCanEditWorkOrder = <T extends Pick<WorkOrder, 'workOrderStateName'> | undefined>(
  workOrder?: T,
): {
  /**
   * If `workOrder` was not passed into the hook, it must be passed into this `canEditWorkOrder` call.
   */
  canEditWorkOrder: T extends undefined
    ? (action: WorkOrderAction, workOrder: Pick<WorkOrder, 'workOrderStateName'>) => boolean
    : (action: WorkOrderAction) => boolean;
} => {
  const { roles } = useUser();
  const { getWorkOrderStatesFromTruthTable } = useGetWorkOrderStatesFromTruthTable();
  const workOrderStateName = useMemo(() => workOrder?.workOrderStateName, [workOrder]);

  const canEditWorkOrder = useCallback(
    (action: WorkOrderAction, overrideWorkOrder?: Pick<WorkOrder, 'workOrderStateName'>) => {
      const state = overrideWorkOrder?.workOrderStateName ?? workOrderStateName;
      if (!state) {
        throw new Error('Work order was not passed in.');
      }
      return (
        getWorkOrderStatesFromTruthTable(action).includes(state) &&
        (!preventAdminActions.includes(action) || !roles.includes('Company.Administrator'))
      );
    },
    [getWorkOrderStatesFromTruthTable, roles, workOrderStateName],
  );

  return { canEditWorkOrder };
};
